/**
 * TRTF — "Complete the Fit" bundle.
 * ---------------------------------------------------------------------------
 * Three jobs:
 *
 *   1. Keep row 1's size in step with the real variant picker, so the bundle
 *      can never add a size the shopper did not choose.
 *   2. Do the arithmetic in the shopper's currency without ever inventing a
 *      number — every price starts life as a cents value Shopify printed.
 *   3. Add the items and hand off to /cart?discount=CODE so the discount is
 *      genuinely on the cart, not just drawn on the page.
 *
 * On (3): if the merchant set BUNDLE up as an automatic discount rather than
 * a code, Shopify quietly ignores the parameter and applies the automatic
 * rule instead. Either configuration lands in the same place, which is why
 * the redirect is unconditional.
 *
 * The gift row is never posted to the cart. The gift app owns that.
 */
(function () {
  /**
   * Shopify's money_format is a template string, not a locale. Rendering it
   * ourselves is the only way to keep a computed total looking identical to
   * the prices Liquid printed next to it.
   */
  function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format && format.match(placeholder);
    const kind = match ? match[1] : 'amount';

    function withDelimiters(number, precision, thousands, decimal) {
      const fixed = (number / 100.0).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    let out;
    switch (kind) {
      case 'amount_no_decimals':
        out = withDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = withDelimiters(value, 2, "'", '.');
        break;
      default:
        out = withDelimiters(value, 2, ',', '.');
    }
    return format ? format.replace(placeholder, out) : out;
  }

  class Bundle {
    constructor(root) {
      this.root = root;
      this.code = root.dataset.code || 'BUNDLE';
      this.percent = parseFloat(root.dataset.percent) || 0;
      this.minItems = parseInt(root.dataset.minItems, 10) || 2;
      this.giftThreshold = parseInt(root.dataset.giftThreshold, 10) || 0;
      this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';

      this.variants = this.readVariants();
      this.currentRow = root.querySelector('[data-role="current"]');
      this.partnerRow = root.querySelector('[data-role="partner"]');
      this.cta = root.querySelector('[data-bundle-add]');
      this.ctaLabel = root.querySelector('[data-bundle-cta-label]');
      this.errorEl = root.querySelector('[data-bundle-error]');

      this.bindSizes();
      this.watchVariantPicker();
      this.cta.addEventListener('click', () => this.addToCart());
      this.render();
    }

    readVariants() {
      const node = this.root.querySelector('[data-bundle-variants]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        // A malformed map must not take the whole block down; without it the
        // row simply stops mirroring and keeps showing the loaded variant.
        return {};
      }
    }

    /** The live <input name="id"> is the single source of truth for row 1. */
    get currentVariantInput() {
      return document.querySelector('form[action*="/cart/add"] input[name="id"]');
    }

    bindSizes() {
      this.root.querySelectorAll('.trtf-bundle__size').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.root.querySelectorAll('.trtf-bundle__size').forEach((b) => {
            b.classList.toggle('is-selected', b === btn);
          });
          const priceEl = this.partnerRow.querySelector('[data-bundle-price]');
          priceEl.dataset.cents = btn.dataset.cents;
          priceEl.textContent = formatMoney(btn.dataset.cents, this.moneyFormat);
          this.render();
        });
      });
    }

    /**
     * Themes swap the whole buy box on variant change, so a change listener
     * on the input alone is not enough — the node it was bound to is gone.
     * Watching the subtree survives the swap.
     *
     * BUT: this bundle renders INSIDE that same container, and render()
     * writes into it. An observer watching its own output is an infinite
     * loop, and this one froze the whole browser tab — every product page
     * on the theme locked up until it was fixed. Three guards:
     *
     *   1. Ignore any mutation whose target is inside our own root.
     *   2. Do not watch attributes at all: far too noisy, and nothing we
     *      need to react to is an attribute change.
     *   3. Coalesce to one sync per animation frame.
     */
    watchVariantPicker() {
      const form = document.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      const container = form.closest('.product__info-container') || form.parentElement || form;

      this.observer = new MutationObserver((records) => {
        const fromOutside = records.some((r) => !this.root.contains(r.target));
        if (fromOutside) this.queueSync();
      });
      this.observer.observe(container, { childList: true, subtree: true });
      container.addEventListener('change', () => this.queueSync());
    }

    /** At most one sync per animation frame, however many mutations land. */
    queueSync() {
      if (this.syncPending) return;
      this.syncPending = true;
      setTimeout(() => {
        this.syncPending = false;
        this.syncCurrent();
      });
    }

    /**
     * Writing an identical value still counts as a mutation. Every write
     * goes through here, as belt and braces alongside the guards above.
     */
    setText(el, value) {
      if (el && el.textContent !== value) el.textContent = value;
    }

    /** Mirror the chosen size and its price into row 1. */
    syncCurrent() {
      const input = this.currentVariantInput;
      if (!input) {
        this.render();
        return;
      }
      const variant = this.variants[String(input.value)];
      if (!variant) {
        this.render();
        return;
      }
      const label = this.currentRow.querySelector('[data-bundle-current-size]');
      if (label) label.textContent = variant.title;

      const priceEl = this.currentRow.querySelector('[data-bundle-price]');
      if (priceEl) {
        priceEl.dataset.cents = variant.price;
        priceEl.textContent = formatMoney(variant.price, this.moneyFormat);
      }
      this.render();
    }

    subtotalCents() {
      let total = 0;
      this.root.querySelectorAll('[data-bundle-item] [data-bundle-price]').forEach((el) => {
        total += parseInt(el.dataset.cents, 10) || 0;
      });
      return total;
    }

    render() {
      const subtotal = this.subtotalCents();
      const items = this.root.querySelectorAll('[data-bundle-item]').length;
      // Rounded to the cent the same way Shopify rounds a percentage
      // discount, so the two do not drift apart by a penny.
      const off = items >= this.minItems ? Math.round((subtotal * this.percent) / 100) : 0;

      const set = (sel, cents) => {
        const el = this.root.querySelector(sel);
        if (el) el.textContent = formatMoney(cents, this.moneyFormat);
      };
      set('[data-bundle-subtotal]', subtotal);

      const offEl = this.root.querySelector('[data-bundle-off]');
      if (offEl) offEl.textContent = '−' + formatMoney(off, this.moneyFormat);

      set('[data-bundle-total]', subtotal - off);

      this.renderGift(subtotal - off);
    }

    /**
     * The gift threshold is measured against what the shopper will actually
     * be charged for these items, not the pre-discount figure — quoting the
     * higher number would promise a gift the order might not qualify for.
     */
    renderGift(payableCents) {
      const status = this.root.querySelector('[data-bundle-gift-status]');
      if (!status || !this.giftThreshold) return;

      const shortfall = this.giftThreshold - payableCents;
      if (shortfall <= 0) {
        status.textContent = 'Unlocked — added free at checkout';
        status.classList.add('is-unlocked');
      } else {
        status.textContent =
          formatMoney(shortfall, this.moneyFormat) + ' away from the free gift';
        status.classList.remove('is-unlocked');
      }
    }

    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    async addToCart() {
      const input = this.currentVariantInput;
      const selected = this.root.querySelector('.trtf-bundle__size.is-selected');

      if (!input || !input.value) {
        this.setError('Choose a size above first.');
        return;
      }
      if (!selected) {
        this.setError('Pick a size for the second piece.');
        return;
      }

      this.setError('');
      this.cta.disabled = true;
      const original = this.ctaLabel.textContent;
      this.ctaLabel.textContent = 'ADDING…';

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      try {
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              { id: Number(input.value), quantity: 1 },
              { id: Number(selected.dataset.variantId), quantity: 1 }
            ]
          })
        });

        if (!response.ok) {
          const problem = await response.json().catch(() => ({}));
          throw new Error(problem.description || 'That did not go through.');
        }

        await this.applyDiscount(root);
        await this.openCart(root);
      } catch (error) {
        this.setError(error.message + ' Try again, or add the pieces separately.');
      } finally {
        this.cta.disabled = false;
        this.ctaLabel.textContent = original;
      }
    }

    /**
     * Put the code on the cart without navigating anywhere.
     *
     * Requesting /discount/CODE attaches it to the session server-side. This
     * is what makes the total above true rather than decorative, and it is
     * verified, not assumed: on a $140.00 cart /cart.js came back with
     * total_price 12600, total_discount 1400 and a discount_code application
     * titled BUNDLE. So the drawer shows the real number, not a promise.
     *
     * If it fails we swallow it. The shopper's items are already in the cart
     * and the code still works at checkout - losing the bundle over a failed
     * side request would be the worse outcome.
     */
    async applyDiscount(root) {
      if (!this.code) return;
      try {
        await fetch(root + 'discount/' + encodeURIComponent(this.code), {
          credentials: 'same-origin'
        });
      } catch (error) {
        /* non-fatal, see above */
      }
    }

    /**
     * Open whatever cart this store actually uses.
     *
     * UpCart owns the drawer here, but every hook is checked at call time
     * rather than assumed to exist. If the cart app is ever swapped out this
     * degrades through the theme's own drawer, then the header cart button,
     * then the cart page - it never dead-ends with items added and no
     * feedback.
     */
    async openCart(root) {
      if (typeof window.upcartRefreshCart === 'function') {
        try {
          await window.upcartRefreshCart();
        } catch (error) {
          /* refresh is best-effort; opening still shows current contents */
        }
      }

      // Let the theme and any other app update their own cart counts.
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      if (typeof window.upcartOpenCart === 'function') {
        window.upcartOpenCart();
        return;
      }

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }

      const trigger = document.querySelector('#cart-icon-bubble, .header__cart, .cart-link');
      if (trigger) {
        trigger.click();
        return;
      }

      window.location.href = root + 'cart';
    }
  }

  function init() {
    document.querySelectorAll('[data-trtf-bundle]').forEach((el) => new Bundle(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
